import { forwardRef, ButtonHTMLAttributes, cloneElement } from 'react'
import { Loader } from 'src/components'

export type ButtonTheme = 'primary' | 'secondary' | 'secondary-ghost' | 'ghost'
export type ButtonSize = 'xs' | 's' | 'm'

const buttonThemes: Record<ButtonTheme, string[]> = {
  primary: [
    'bg-black',
    'text-white',
    'disabled:bg-dark-gray',
    'disabled:text-white',
    'disabled:border-dark-gray',
  ],
  secondary: [
    'bg-white',
    'text-black',
    'hover:bg-light-gray',
    'disabled:text-dark-gray',
    'disabled:border-dark-gray',
  ],
  'secondary-ghost': [
    'bg-white',
    'text-dark-gray',
    'hover:bg-light-gray',
    'hover:!text-black',
    'border-gray',
    'flex-row-reverse',
  ],
  ghost: [
    'bg-transparent',
    'text-dark-gray',
    'border-transparent',
    'hover:!text-black',
  ],
}

const iconThemes: Record<ButtonTheme, string[]> = {
  primary: ['fill-white'],
  secondary: ['fill-black'],
  'secondary-ghost': ['fill-dark-gray group-hover:fill-black'],
  ghost: [
    'fill-dark-gray',
    'group-hover:fill-black',
    'transition-all',
    'duration-500',
  ],
}

const buttonSizes: Record<ButtonSize, string[]> = {
  xs: ['px-md', 'py-[5px]'],
  s: ['px-md', 'py-[7px]'],
  m: ['px-md', 'py-[12px]'],
}

const buttonIconOnlySizes: Record<ButtonSize, string[]> = {
  xs: ['p-[5px]'],
  s: ['p-[9px]'],
  m: ['p-[14px]'],
}
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  theme?: ButtonTheme
  size?: ButtonSize
  icon?: JSX.Element
  isLoading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      theme = 'primary',
      className,
      disabled,
      icon: Icon,
      isLoading = false,
      size = 's',
      ...props
    },
    ref
  ) => {
    const iconOnly = Icon && !children
    const classes = [
      'rounded-md',
      'border-black',
      'border-s',
      'transition-colors',
      'focus:outline-none',
      'select-none',
      'small-strong',
      'flex',
      'items-center',
      'gap-[10px]',
      'whitespace-nowrap',
      'group',
      'transition-all duration-500',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      Icon ? 'justify-between' : 'justify-center',
      iconOnly
        ? buttonIconOnlySizes[size].join(' ')
        : buttonSizes[size].join(' '),
      ...buttonThemes[theme],
    ]
    if (className) classes.push(className)

    return (
      <button
        ref={ref}
        disabled={disabled}
        className={classes.join(' ')}
        {...props}
      >
        {children}
        {isLoading ? (
          <Loader buttonType theme={theme === 'primary' ? 'light' : 'dark'} />
        ) : (
          Icon &&
          cloneElement(Icon, {
            className: `transition-all duration-500 ${iconThemes[theme].join(
              ' '
            )}`,
          })
        )}
      </button>
    )
  }
)

export default Button
